import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CRM_ACCOUNT_SOURCES, CRM_LIFECYCLES, CRM_OPERATOR_TYPES,
  type CrmAccount, type CrmAccountDetail, type CrmAccountDto,
} from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n';
import { useToast } from '../../components/Toast';
import { Button, Card, Checkbox, Chip, Empty, Input, Loading, Modal, PageHeader, Select, Textarea } from '../../components/ui';
import { NewSegmentInline, SegmentsModal } from './CrmSegments';
import { crmLabel, crmOptions, lifecycleTone, segmentLabel, segmentOptions, useOwners, useSegments } from '../../lib/crm';

export function CrmAccounts() {
  const { company } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const owners = useOwners();
  const segments = useSegments();
  const [params, setParams] = useSearchParams();
  const filter = (k: string) => params.get(k) ?? '';
  const setFilter = (k: string, v: string) => { const next = new URLSearchParams(params); if (v) next.set(k, v); else next.delete(k); setParams(next, { replace: true }); };
  const qs = new URLSearchParams();
  for (const k of ['segment', 'country', 'priority', 'lifecycle', 'ownerUserId', 'q', 'hasTrigger', 'outreach']) if (filter(k)) qs.set(k, filter(k));
  qs.set('limit', '500');
  const accounts = useQuery({ queryKey: ['crm-accounts', company?.id, qs.toString()], queryFn: () => capi<CrmAccount[]>(`/crm/accounts?${qs}`) });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [managingSegments, setManagingSegments] = useState(false);
  const [bulkOwner, setBulkOwner] = useState('');

  const assign = useMutation({
    mutationFn: async () => {
      for (const id of selected) await capi(`/crm/accounts/${id}`, { method: 'PATCH', json: { ownerUserId: bulkOwner || null } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm-accounts'] }); setSelected(new Set()); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const rows = accounts.data ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const ownerOptions = [{ value: '', label: t('noOwner') }, ...(owners.data ?? []).map((o) => ({ value: o.userId, label: o.name }))];

  return (
    <>
      <PageHeader crumb={t('crmCrumb')} title={t('accTitle')} sub={t('accSub')} actions={<>
        <Button icon="fas fa-layer-group" onClick={() => setManagingSegments(true)}>{t('segManage')}</Button>
        <Button variant="primary" icon="fas fa-plus" onClick={() => setCreating(true)}>{t('accNew')}</Button>
      </>} />
      <div className="stack">
        <Card>
          <div className="filters">
            <Input label={t('filterSearch')} value={filter('q')} onChange={(e) => setFilter('q', e.target.value)} />
            <Select label={t('colSegment')} options={segmentOptions(segments.data, lang, t('filterAll'), filter('segment'))} value={filter('segment')} onChange={(e) => setFilter('segment', e.target.value)} />
            <Input label={t('colCountry')} maxLength={2} value={filter('country')} onChange={(e) => setFilter('country', e.target.value.toUpperCase())} style={{ width: 70 }} />
            <Select label={t('colPriority')} options={[{ value: '', label: t('filterAll') }, { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }]} value={filter('priority')} onChange={(e) => setFilter('priority', e.target.value)} />
            <Select label={t('colLifecycle')} options={crmOptions('lifecycle', CRM_LIFECYCLES, lang, t('filterAll'))} value={filter('lifecycle')} onChange={(e) => setFilter('lifecycle', e.target.value)} />
            <Select label={t('colOwner')} options={[{ value: '', label: t('filterAll') }, ...(owners.data ?? []).map((o) => ({ value: o.userId, label: o.name }))]} value={filter('ownerUserId')} onChange={(e) => setFilter('ownerUserId', e.target.value)} />
            <Checkbox label={t('filterHasTrigger')} checked={filter('hasTrigger') === 'true'} onChange={(e) => setFilter('hasTrigger', e.target.checked ? 'true' : '')} />
            <Checkbox label={t('filterOutreach')} checked={filter('outreach') === 'true'} onChange={(e) => setFilter('outreach', e.target.checked ? 'true' : '')} />
          </div>
        </Card>
        {selected.size > 0 && (
          <div className="banner tint-accent row">
            <span>{t('selectedN', { n: selected.size })}</span>
            <select className="inline" aria-label={t('assignOwner')} value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)}>
              {ownerOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Button size="sm" variant="primary" icon="fas fa-user-tag" disabled={assign.isPending} onClick={() => assign.mutate()}>{t('assignOwner')}</Button>
          </div>
        )}
        <Card pad={false} className="table">
          {!accounts.data ? <Loading error={accounts.error} /> : (
            <table>
              <thead><tr>
                <th style={{ width: 28 }}><input type="checkbox" aria-label={t('filterAll')} checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))} /></th>
                <th>{t('colName')}</th><th>{t('colCountry')}</th><th>{t('colSegment')}</th><th>{t('colPriority')}</th><th>{t('colLifecycle')}</th><th>{t('colOwner')}</th>
                <th className="r">{t('colPeople')}</th><th className="r">{t('colOpenDeals')}</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty />}
                {rows.map((a) => (
                  <tr key={a.id} className="click" onClick={() => navigate(`/crm/accounts/${a.id}`)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={a.name} checked={selected.has(a.id)} onChange={() => { const n = new Set(selected); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); setSelected(n); }} />
                    </td>
                    <td><div style={{ fontWeight: 600 }}>{a.name}</div><div className="sub">{a.triggerNote ? <><i className="fas fa-bolt" aria-hidden="true" /> {a.triggerNote}</> : a.website ?? ''}</div></td>
                    <td className="ref">{a.country}</td>
                    <td className="secondary small">{segmentLabel(segments.data, a.segment, lang)}</td>
                    <td>{a.priority ?? '—'}</td>
                    <td><Chip tone={lifecycleTone(a.lifecycle)}>{crmLabel('lifecycle', a.lifecycle, lang)}</Chip></td>
                    <td className="secondary small">{a.ownerName ?? '—'}</td>
                    <td className="r">{a.peopleCount}</td>
                    <td className="r">{a.openDealCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      {managingSegments && <SegmentsModal onClose={() => setManagingSegments(false)} />}
      {creating && <AccountForm onClose={() => setCreating(false)} onSaved={(a) => navigate(`/crm/accounts/${a.id}`)} />}
    </>
  );
}

type AccountFormState = Record<keyof CrmAccountDto, string>;

/** Create (no `account`) or edit an account. Empty fields are sent as null so a cleared field is cleared. */
export function AccountForm({ account, onClose, onSaved }: { account?: CrmAccountDetail; onClose: () => void; onSaved: (a: CrmAccountDetail) => void }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const owners = useOwners();
  const segments = useSegments();
  const init = (k: keyof CrmAccount) => (account?.[k] == null ? '' : String(account[k]));
  const [f, setF] = useState<AccountFormState>({
    name: init('name'), country: init('country'), website: init('website'), segment: init('segment') || 'other', operatorType: init('operatorType'),
    fleetSize: init('fleetSize'), mainAircraft: init('mainAircraft'), aocReference: init('aocReference'), priority: init('priority'),
    triggerNote: init('triggerNote'), smsApproach: init('smsApproach'), source: init('source') || 'manual', sourceRef: init('sourceRef'),
    sourceUrl: init('sourceUrl'), ownerUserId: init('ownerUserId'), notes: init('notes'), emailDomain: init('emailDomain'),
  });
  const set = (k: keyof AccountFormState) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() === '' ? null : v])),
        name: f.name, country: f.country, segment: f.segment, source: f.source, priority: f.priority ? Number(f.priority) : null,
      };
      return account
        ? capi<CrmAccountDetail>(`/crm/accounts/${account.id}`, { method: 'PATCH', json: body })
        : capi<CrmAccountDetail>('/crm/accounts', { method: 'POST', json: body });
    },
    onSuccess: (a) => { qc.invalidateQueries({ queryKey: ['crm-accounts'] }); qc.setQueryData(['crm-account', a.id], a); toast.push(t('done')); onSaved(a); onClose(); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Modal title={account ? account.name : t('accNew')} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid fields">
          <Input label={t('fName')} required value={f.name} onChange={set('name')} />
          <Input label={t('fCountry')} required minLength={2} maxLength={2} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} />
          <Input label={t('fWebsite')} value={f.website} onChange={set('website')} />
          <Input label={t('fEmailDomain')} value={f.emailDomain} onChange={set('emailDomain')} placeholder="voorbeeld.nl" />
          <div className="stack">
            <Select label={t('fSegment')} options={segmentOptions(segments.data, lang, undefined, f.segment)} value={f.segment} onChange={set('segment')} />
            <NewSegmentInline onAdded={(key) => setF((prev) => ({ ...prev, segment: key }))} />
          </div>
          <Select label={t('fOperatorType')} options={crmOptions('operatorType', CRM_OPERATOR_TYPES, lang, '—')} value={f.operatorType} onChange={set('operatorType')} />
          <Select label={t('fPriority')} options={[{ value: '', label: '—' }, { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }]} value={f.priority} onChange={set('priority')} />
          <Input label={t('fFleet')} value={f.fleetSize} onChange={set('fleetSize')} />
          <Input label={t('fAircraft')} value={f.mainAircraft} onChange={set('mainAircraft')} />
          <Input label={t('fAoc')} value={f.aocReference} onChange={set('aocReference')} />
          <Select label={t('fOwner')} options={[{ value: '', label: t('noOwner') }, ...(owners.data ?? []).map((o) => ({ value: o.userId, label: o.name }))]} value={f.ownerUserId} onChange={set('ownerUserId')} />
          <Select label={t('fSource')} options={crmOptions('source', CRM_ACCOUNT_SOURCES, lang)} value={f.source} onChange={set('source')} />
          <Input label={t('fSourceRef')} value={f.sourceRef} onChange={set('sourceRef')} />
        </div>
        <Input label={t('fSourceUrl')} value={f.sourceUrl} onChange={set('sourceUrl')} />
        <Input label={t('fTrigger')} value={f.triggerNote} onChange={set('triggerNote')} />
        <Textarea label={t('fSms')} value={f.smsApproach} onChange={set('smsApproach')} />
        <Textarea label={t('fNotes')} value={f.notes} onChange={set('notes')} />
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>{account ? t('crmSave') : t('crmCreate')}</Button>
        </div>
      </form>
    </Modal>
  );
}
